import { FaRegUserCircle, FaArrowRight } from 'react-icons/fa';
import { IoInformationCircle, IoMailOutline } from 'react-icons/io5';
import { BsGlobe } from 'react-icons/bs';
import './trcForm.css';
import useAccess from '../../../../hooks/useAccess';
import { userInfoAtom } from '../../../../../data/atoms/utilsAtoms';
import { useAtomValue } from 'jotai';
import GetUserRole from '../../utils/getUserRole';
import Toast from '../../../../../utils/toastManager';
import { useForm } from 'react-hook-form';
import { useEffect } from 'react';
import ACCESS_STATUS from '../../../../constants/accessStatus';
import TRCRequestButton from '../trcButton/trcButton';
import normalizeWebsiteLink from '@/TRC/pages/Access/utils/normalizeWebsiteLink';
import { isValidUrlOrInstagram } from '@/utils/validation';
import { IconButton, Tooltip } from '@mui/material';
import { trcUserAtom } from '@/TRC/atoms/trc-global';
import { useNavigate } from 'react-router-dom';
import { buildRoutes } from '@/service/buildRoutes';

const TRCForm = () => {
	const userData = useAtomValue(userInfoAtom);
	const trcUser = useAtomValue(trcUserAtom);
	const navigate = useNavigate();
	if (!userData) {
		Toast.error('please login to continue');
	}
	const role = GetUserRole();
	const { requestAccess } = useAccess();

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors, isSubmitting }
	} = useForm({
		defaultValues: {
			fullName: '',
			email: '',
			website: '',
			role: [],
			gain: ''
		}
	});

	useEffect(() => {
		if (!userData) return;
		if (trcUser?.accessStatus === ACCESS_STATUS.PENDING) {
			reset({
				fullName: trcUser.name,
				email: trcUser.email,
				website: trcUser.link,
				role: trcUser.role || [],
				gain: trcUser.gain || ''
			});
		} else {
			reset({
				fullName: `${userData.user?.firstname ?? ''} ${userData.user?.lastname ?? ''}`.trim(),
				email: userData.user?.email ?? '',
				website: '',
				role: role ? [role] : [],
				gain: ''
			});
		}
	}, [userData, trcUser, role, reset]);

	const validationRules = {
		fullName: {
			required: 'Full name is required'
		},
		email: {
			required: 'Email address is required',
			pattern: {
				value: /\S+@\S+\.\S+/,
				message: 'Please enter a valid email address'
			}
		},
		website: {
			required: 'Instagram or Website is required',
			validate: v => {
				return isValidUrlOrInstagram(v) || 'Enter a valid URL or Instagram handle';
			}
		},
		role: {
			validate: value => value?.length > 0 || 'Please select at least one role'
		}
	};

	const onSubmit = async values => {
		const normalizedLink = normalizeWebsiteLink(values.website.trim());
		try {
			await requestAccess({
				name: values.fullName,
				email: values.email,
				link: normalizedLink,
				role: values.role,
				gain: values.gain,
				accessStatus: ACCESS_STATUS.PENDING
			});
			reset();
			navigate(buildRoutes.toTRCAccess());
		} catch (e) {
			Toast.error(e.message || 'Request failed. Please try again.');
		}
	};

	return (
		<form className="trc-form" onSubmit={handleSubmit(onSubmit)}>
			<div className="trc-form-grid">
				<div className="trc-form-field">
					<label>FULL NAME *</label>
					<div className="input-wrapper">
						<input
							type="text"
							{...register('fullName', validationRules.fullName)}
							placeholder="John Doe"
							className={errors.fullName ? 'error' : ''}
							readOnly
						/>
						<FaRegUserCircle className="icon" />
					</div>
					{errors.fullName && <span className="error-message">{errors.fullName.message}</span>}
				</div>

				<div className="trc-form-field">
					<label>EMAIL ADDRESS *</label>
					<div className="input-wrapper">
						<input
							type="email"
							{...register('email', validationRules.email)}
							placeholder="t7t5b@example.com"
							className={errors.email ? 'error' : ''}
							readOnly
						/>
						<IoMailOutline className="icon" />
					</div>
					{errors.email && <span className="error-message">{errors.email.message}</span>}
				</div>

				<div className="trc-form-field">
					<label className="position-relative">
						INSTAGRAM OR WEBSITE *{' '}
						<Tooltip
							title="We ask for an Instagram or website to verify that you are an active instructor. This helps us ensure the Teacher Resource Center remains a trusted space exclusively for teachers."
							placement="top-start"
							className="position-absolute translate-middle"
							style={{ top: '30%', right: '10%' }}
							slotProps={{ tooltip: { sx: { fontSize: '0.88rem' } } }}
						>
							<IconButton size="small">
								<IoInformationCircle color="#3CB4E4" />
							</IconButton>
						</Tooltip>
					</label>
					<div className="input-wrapper">
						<input
							type="text"
							{...register('website', validationRules.website)}
							placeholder="e.g. @john_doe, john-doe.com, etc."
							className={errors.website ? 'error' : ''}
						/>
						<BsGlobe className="icon" />
					</div>
					{errors.website && <span className="error-message">{errors.website.message}</span>}
				</div>

				<div className="trc-form-field">
					<label>WHAT IS YOUR ROLE? *</label>
					<div className="checkbox-group">
						<label>
							<input type="checkbox" value="instructor" {...register('role', validationRules.role)} />
							INSTRUCTOR / COACH
						</label>
						<label>
							<input type="checkbox" value="studio-owner" {...register('role', validationRules.role)} />
							STUDIO OWNER / DIRECTOR
						</label>
					</div>
					{errors.role && <span className="error-message">{errors.role.message}</span>}
					<p className="mandatory-note" style={{ textAlign: 'left', fontStyle: 'italic' }}>
						CHECK ALL THAT APPLY
					</p>
				</div>

				<div className="trc-form-field full-span">
					<label>WHAT ARE YOU HOPING TO GAIN FROM TEACHER RESOURCE CENTER?</label>
					<textarea placeholder="Optional" {...register('gain')} />
				</div>
			</div>

			<p className="mandatory-note mb-5">* FIELDS ARE MANDATORY</p>
			<div className="d-flex flex-column flex-md-row gap-md-1 justify-content-center">
				<TRCRequestButton
					type="submit"
					icon={<FaArrowRight />}
					iconPosition="right"
					disabled={!userData || isSubmitting}
				>
					{isSubmitting
						? 'Submitting...'
						: trcUser?.accessStatus === ACCESS_STATUS.PENDING
							? 'Resubmit Application'
							: 'Request Access'}
				</TRCRequestButton>
				{trcUser?.accessStatus === ACCESS_STATUS.PENDING && (
					<TRCRequestButton
						type="button"
						onClick={e => {
							e.preventDefault();
							navigate(buildRoutes.toTRCAccess());
						}}
						variant="secondary"
					>
						Back to Pending Page
					</TRCRequestButton>
				)}
			</div>
		</form>
	);
};

export default TRCForm;
